import React, { useState, useEffect } from 'react';
import { getCareers, createCareer, updateCareer, deleteCareer, toggleCareerStatus, getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import CareerForm from '../forms/CareerForm';
import toast from 'react-hot-toast';

const CareersModule = () => {
  const [careers, setCareers] = useState([]);
  const [careerCategories, setCareerCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all'); // Changed to 'all' as default
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const columns = ['Title', 'Department', 'Location', 'Type', 'Experience', 'Status', 'Actions'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch careers and categories in parallel
      const [careersResponse, categoriesResponse] = await Promise.all([
        getCareers(),
        getCategories()
      ]);
      
      // Process careers
      let careersData = careersResponse.data || careersResponse || [];
      setCareers(careersData);
      
      // Process categories - filter only career type
      let categoriesData = [];
      if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        categoriesData = categoriesResponse.data.filter(cat => cat.type === 'career');
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesData = categoriesResponse.filter(cat => cat.type === 'career');
      }
      
      setCareerCategories(categoriesData);
      // Don't auto-select a category, show all by default
      
      console.log('Careers loaded:', careersData.length);
      console.log('Career categories loaded:', categoriesData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching careers');
      setCareers([]);
      setCareerCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      try {
        await deleteCareer(id);
        toast.success('Job posting deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Error deleting job posting');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleCareerStatus(id);
      toast.success(`Job posting ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Error toggling job status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Filter careers by selected category - show all if 'all' is selected
  const filteredCareers = selectedCategory === 'all' || !selectedCategory
    ? careers
    : careers.filter(career => career.department === selectedCategory);

  // Get unique departments from careers for filter options
  const uniqueDepartments = [...new Set(careers.map(career => career.department).filter(Boolean))];

  if (loading) {
    return <LoadingSpinner message="Loading careers..." />;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Career Opportunities</h2>
        <p style={{ color: '#5A6E7F' }}>Manage job postings and career opportunities</p>
      </div>

      {/* Category Filter Bar */}
      {(careerCategories.length > 0 || uniqueDepartments.length > 0) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          background: '#F7F9FC',
          borderRadius: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500, color: '#0A192F' }}>
              <i className="fas fa-filter"></i> Filter by Department:
            </span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                background: 'white',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">-- All Departments ({careers.length}) --</option>
              {uniqueDepartments.map((dept, idx) => (
                <option key={idx} value={dept}>
                  {dept} ({careers.filter(c => c.department === dept).length})
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '0.5rem 1rem',
                background: viewMode === 'grid' ? '#2C7A7B' : '#FFFFFF',
                color: viewMode === 'grid' ? 'white' : '#5A6E7F',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s'
              }}
            >
              <i className="fas fa-th-large"></i> Grid View
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={{
                padding: '0.5rem 1rem',
                background: viewMode === 'table' ? '#2C7A7B' : '#FFFFFF',
                color: viewMode === 'table' ? 'white' : '#5A6E7F',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s'
              }}
            >
              <i className="fas fa-table"></i> Table View
            </button>
          </div>
        </div>
      )}

      {/* Add Job Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ 
            padding: '0.6rem 1.2rem', 
            background: '#2C7A7B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '10px', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
        >
          <i className="fas fa-plus"></i> Add New Job
        </button>
      </div>

      {/* Stats */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#5A6E7F', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-briefcase" style={{ color: '#2C7A7B' }}></i>
          <strong>{filteredCareers.length}</strong> {filteredCareers.length === 1 ? 'job posting' : 'job postings'} found
          {selectedCategory !== 'all' && selectedCategory && <span> in <strong>{selectedCategory}</strong></span>}
        </p>
      </div>

      {filteredCareers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '20px', border: '2px dashed #E2E8F0' }}>
          <i className="fas fa-briefcase" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
          <p style={{ color: '#5A6E7F', marginBottom: '1rem', fontSize: '1.1rem' }}>No job postings found</p>
          <button onClick={() => { setEditingItem(null); setShowModal(true); }} style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>
            <i className="fas fa-plus"></i> Create Your First Job Posting
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredCareers.map((career) => (
            <CareerCard
              key={career._id}
              career={career}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCareers.map((career) => (
                <tr key={career._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{career.title}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ background: '#E3F2FD', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem' }}>
                      {career.department}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>{career.location}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      background: career.type === 'Full-time' ? '#D4EDDA' : career.type === 'Part-time' ? '#FFF3CD' : '#E2E3E5',
                      padding: '0.2rem 0.6rem', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem' 
                    }}>
                      {career.type}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>{career.experience || 'Not specified'}</td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(career._id, career.isActive)} 
                      style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: career.isActive ? '#D4EDDA' : '#F8D7DA', color: career.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {career.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(career)} 
                      style={{ padding: '0.35rem 0.8rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '0.75rem' }}
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(career._id)} 
                      style={{ padding: '0.35rem 0.8rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CareerForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchData} 
          careerCategories={careerCategories}
        />
      )}
    </div>
  );
};

// Career Card Component
const CareerCard = ({ career, onEdit, onDelete, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get full image URL
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        border: '1px solid #E2E8F0',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Status Badge */}
      <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 1 }}>
        <button
          onClick={() => onToggle(career._id, career.isActive)}
          style={{
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            border: 'none',
            background: career.isActive ? '#10B981' : '#EF4444',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: 600
          }}
        >
          {career.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      {/* Job Image */}
      {career.imageUrl && (
        <div style={{ height: '140px', overflow: 'hidden' }}>
          <img 
            src={getFullImageUrl(career.imageUrl)} 
            alt={career.title} 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      <div style={{ padding: '1.5rem' }}>
        <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{
            background: '#E3F2FD',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.7rem',
            color: '#2C7A7B',
            fontWeight: 600
          }}>
            {career.department}
          </span>
          <span style={{
            background: career.type === 'Full-time' ? '#D4EDDA' : career.type === 'Part-time' ? '#FFF3CD' : '#E2E3E5',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.7rem',
            color: career.type === 'Full-time' ? '#155724' : '#856404',
            fontWeight: 600
          }}>
            {career.type}
          </span>
        </div>

        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#0A192F',
          marginBottom: '0.5rem',
          lineHeight: '1.3'
        }}>
          {career.title}
        </h3>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#5A6E7F' }}>
          <span><i className="fas fa-map-marker-alt"></i> {career.location}</span>
          <span><i className="fas fa-clock"></i> {career.experience || 'Experience not specified'}</span>
        </div>

        <p style={{
          fontSize: '0.85rem',
          color: '#5A6E7F',
          lineHeight: '1.5',
          marginBottom: '1rem',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {career.description?.substring(0, 120) || 'No description available'}...
        </p>

        <div style={{ marginBottom: '1rem', paddingTop: '0.5rem', borderTop: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '0.75rem', color: '#2C7A7B', fontWeight: 600 }}>
            <i className="fas fa-tag"></i> {career.salary || 'Salary negotiable'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => onEdit(career)}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: '#2C7A7B',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            onClick={() => onDelete(career._id)}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: '#DC2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
          >
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareersModule;